import { useCallback, useEffect } from 'react';

interface HotkeyOptions {
    /** Whether the hotkey is enabled */
    enabled?: boolean;
    /** Whether to prevent default browser behaviour */
    preventDefault?: boolean;
    /** Whether to stop event propagation */
    stopPropagation?: boolean;
}

interface KeyCombination {
    key: string;
    altKey?: boolean;
    ctrlKey?: boolean;
    shiftKey?: boolean;
    metaKey?: boolean;
}

/**
 * Parse a hotkey string into a key combination object
 * @example parseHotkey('Alt+Shift+I') => { key: 'i', altKey: true, shiftKey: true }
 */
function parseHotkey(hotkey: string | undefined): KeyCombination {
    if (!hotkey) {
        return { key: '' };
    }

    const parts = hotkey.toLowerCase().split('+');
    const key = parts.pop() ?? '';

    return {
        key,
        altKey: parts.includes('alt'),
        ctrlKey: parts.includes('ctrl') || parts.includes('control'),
        shiftKey: parts.includes('shift'),
        metaKey: parts.includes('meta') || parts.includes('cmd') || parts.includes('command'),
    };
}

/**
 * Check if a keyboard event matches a key combination
 */
function isHotkeyMatch(event: KeyboardEvent, combination: KeyCombination): boolean {
    if (typeof event.key !== 'string') {
        return false;
    }

    return (
        event.key.toLowerCase() === combination.key &&
        event.altKey === (combination.altKey ?? false) &&
        event.ctrlKey === (combination.ctrlKey ?? false) &&
        event.shiftKey === (combination.shiftKey ?? false) &&
        event.metaKey === (combination.metaKey ?? false)
    );
}

/**
 * Hook for registering keyboard shortcuts
 *
 * @param hotkey - The keyboard shortcut string (e.g., 'Cmd+Shift+Z', 'Ctrl+S')
 * @param callback - Function to call when the hotkey is pressed
 * @param options - Configuration options
 *
 * @example
 * ```typescript
 * useHotkey('Cmd+Shift+Z', () => {
 *   console.log('Inspector toggled');
 * });
 *
 * useHotkey('Escape', handleClose, { enabled: isOpen });
 * ```
 */
export function useHotkey(hotkey: string, callback: (event: KeyboardEvent) => void, options: HotkeyOptions = {}): void {
    const { enabled = true, preventDefault = true, stopPropagation = false } = options;

    const combination = parseHotkey(hotkey);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!enabled) return;

            if (isHotkeyMatch(event, combination)) {
                if (preventDefault) {
                    event.preventDefault();
                }
                if (stopPropagation) {
                    event.stopPropagation();
                }
                callback(event);
            }
        },
        [enabled, combination, preventDefault, stopPropagation, callback],
    );

    useEffect(() => {
        if (!enabled) return;

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [enabled, handleKeyDown]);
}
